import React, { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useImmer } from "use-immer";

import { OrganizationDto } from "@intelligems/ig-types";
import { List } from "@mui/material";

import { useConfiguration } from "../../api/configurations/use-configuration";
import { useIsAdmin } from "../../api/user-auths/is-admin";
import { MenuName } from "../../types/navigation";
import {
  checkAllScopes,
  checkAnyScope,
  checkBooleanScope,
  checkSubscriptionStatus,
} from "../ScopeWrappers/helpers";
import { CREATE_DISCOUNT_EXPERIMENT_BUTTON } from "../ScopeWrappers/Scopes";
import { TopRoutes } from "../Utils/routes";

import ListItemLink from "./ListItemLink";
import { shouldSubMenuBeOpen } from "./utils";

interface SideNavBarBodyProps {
  drawerOpen: boolean;
  organization: OrganizationDto;
}

export const SideNavBarBody = ({
  drawerOpen,
  organization,
}: SideNavBarBodyProps) => {
  const location = useLocation();
  const isAdmin = useIsAdmin();
  const isSubscriptionActive = checkSubscriptionStatus(true) ?? false;

  const [subMenuState, setSubMenuState] = useImmer({
    menuAnalytics: shouldSubMenuBeOpen("analytics", location?.pathname),
    widgetLibrary: shouldSubMenuBeOpen("widget", location?.pathname),
  });

  const handleSubMenuToggle = (menu: MenuName) => {
    setSubMenuState((state) => ({ ...state, [menu]: !state[menu] }));
  };

  const isSitewideDashEnabled = useMemo(() => {
    return (
      checkAllScopes([
        isSubscriptionActive,
        checkBooleanScope(
          "should_show_sitewide_dashboard",
          organization.configurations
        ),
      ]) || isAdmin
    );
  }, [isSubscriptionActive, isAdmin, organization.configurations]);

  useEffect(() => {
    setSubMenuState({
      menuAnalytics: shouldSubMenuBeOpen("analytics", location?.pathname),
      widgetLibrary: shouldSubMenuBeOpen("widget", location?.pathname),
    });
  }, [location]);

  return (
    <List style={{ padding: 0 }}>
      <ListItemLink
        Route={TopRoutes().home}
        drawerOpen={drawerOpen}
        hasAccess={true}
      />
      <ListItemLink
        exact={true}
        Route={TopRoutes().experiments}
        drawerOpen={drawerOpen}
        hasAccess={true}
      />
      <ListItemLink
        Route={TopRoutes().personalizations}
        drawerOpen={drawerOpen}
        hasAccess={checkAnyScope([
          isAdmin || false,
          checkBooleanScope("personalizations", organization.configurations),
        ])}
        exact={true}
      />
      {isSitewideDashEnabled && (
        <ListItemLink
          Route={TopRoutes().sitewide}
          drawerOpen={drawerOpen}
          hasAccess={isSitewideDashEnabled}
        />
      )}
      <ListItemLink
        Route={TopRoutes().analytics}
        drawerOpen={drawerOpen}
        open={subMenuState.menuAnalytics}
        handleClick={() => {
          handleSubMenuToggle("menuAnalytics");
        }}
        hasAccess={checkSubscriptionStatus(true) || isAdmin || false}
      />
      <ListItemLink
        Route={TopRoutes().widgetLibrary}
        drawerOpen={drawerOpen}
        open={subMenuState.widgetLibrary}
        handleClick={() => {
          handleSubMenuToggle("menuWidgetLibrary");
        }}
        hasAccess={true}
      />
    </List>
  );
};
